import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { getApiBaseUrl } from '../utils/apiConfig';

type ChallengeKind = 'geolocation' | 'device';

function resolveKind(pathname: string): ChallengeKind {
  return pathname.includes('device') ? 'device' : 'geolocation';
}

function resolveEndpoint(pathname: string, kind: ChallengeKind): string {
  const isAdmin = pathname.startsWith('/admin');
  const suffix = kind === 'device' ? 'confirm-new-device' : 'confirm-geolocation';
  return isAdmin ? `/admin/auth/${suffix}` : `/auth/${suffix}`;
}

const ConfirmLoginChallenge: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const kind = resolveKind(location.pathname);
  const isAdmin = location.pathname.startsWith('/admin');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const code = searchParams.get('code');
    if (!code) {
      setStatus('error');
      setMessage('Missing confirmation code. Open the link from your email.');
      return;
    }

    let cancelled = false;
    (async () => {
      setStatus('loading');
      try {
        const endpoint = resolveEndpoint(location.pathname, kind);
        const response = await fetch(`${getApiBaseUrl()}${endpoint}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code }),
        });
        const json = await response.json().catch(() => ({}));
        if (cancelled) return;
        if (!response.ok) {
          setStatus('error');
          setMessage(json.message || json.errors?.[0] || 'Confirmation failed');
          return;
        }
        setStatus('success');
        setMessage(json.message || 'Confirmed. You can log in again.');
      } catch {
        if (!cancelled) {
          setStatus('error');
          setMessage('Network error while confirming. Please try again.');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [kind, location.pathname, searchParams]);

  const title = kind === 'device' ? 'Confirm new device' : 'Confirm new location';
  const loginPath = isAdmin ? '/admin/login' : '/login';

  return (
    <div className="min-h-screen bg-zazoo-light-grey flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full bg-white rounded-xl shadow-sm border border-gray-200 p-8 space-y-4">
        <h1 className="text-2xl font-semibold text-gray-900">{title}</h1>
        {status === 'loading' && (
          <p className="text-sm text-gray-600">Confirming…</p>
        )}
        {status === 'success' && (
          <>
            <p className="text-sm text-green-700">{message}</p>
            <button
              type="button"
              className="w-full px-4 py-2.5 rounded-full bg-Zogzila-accent-blue text-white text-sm font-medium"
              onClick={() => navigate(loginPath)}
            >
              Go to login
            </button>
          </>
        )}
        {status === 'error' && (
          <>
            <p className="text-sm text-red-600">{message}</p>
            <Link to={loginPath} className="text-sm text-Zogzila-accent-blue underline">
              Back to login
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default ConfirmLoginChallenge;
